import {
  Component,
  ChangeDetectionStrategy,
  OnInit,
  OnDestroy,
} from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { DiallerService } from '@app/dialler/services';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { BehaviorSubject, from, merge, Observable, throwError } from 'rxjs';
import {
  catchError,
  filter,
  map,
  scan,
  take,
  withLatestFrom,
} from 'rxjs/operators';
import { AddressLookupService, AuthService, valuesOf } from '@adreno-common';
import { WorkService } from '../services';
import { StatsModalComponent } from './stats-modal.component';
import { ToastrService } from 'ngx-toastr';
import { AddressLookupComponent } from './address-lookup.component';
import { AddressLookupResult } from '../models';
import { Unity4Claim } from '@adreno-common';
import { SubSink } from 'subsink';
import { masterValidation } from '../code';
import { HttpErrorResponse } from '@angular/common/http';

@Component({
  selector: 'app-contact',
  templateUrl: './contact.component.html',
  styles: [
    `
      :host {
        display: flex;
        flex-direction: column;
        flex-grow: 1;
      }
    `,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ContactComponent implements OnInit, OnDestroy {
  private submitting = new BehaviorSubject<boolean>(false);
  private subs = new SubSink();

  Unity4Claim = Unity4Claim;

  contactFormGroup!: FormGroup;
  postcodeLabel: string = 'Postcode';
  sals = ['Mr', 'Mrs', 'Ms', 'Miss', 'Master', 'Dr', 'Sir', 'Mr & Mrs'];
  stateLabel: string = 'State';
  suburbLabel: string = 'Suburb';
  viewModel$!: Observable<ViewModel>;

  constructor(
    private fb: FormBuilder,
    private router: Router,
    private workSvc: WorkService,
    private diallerSvc: DiallerService,
    private modalSvc: NgbModal,
    private toastr: ToastrService,
    private addressSvc: AddressLookupService,
    private authSvc: AuthService
  ) {}

  private initFormGroup() {
    this.contactFormGroup = this.fb.group({
      company: [
        null,
        Validators.maxLength(masterValidation.company!.maxLength),
      ],
      sal: [null, Validators.maxLength(masterValidation.sal!.maxLength)],
      first: [null, Validators.maxLength(masterValidation.first!.maxLength)],
      last: [null, Validators.maxLength(masterValidation.last!.maxLength)],
      position: [
        null,
        Validators.maxLength(masterValidation.position!.maxLength),
      ],
      address1: [
        null,
        Validators.maxLength(masterValidation.address1!.maxLength),
      ],
      address2: [
        null,
        Validators.maxLength(masterValidation.address2!.maxLength),
      ],
      suburb: [null, Validators.maxLength(masterValidation.suburb!.maxLength)],
      state: [null, Validators.maxLength(masterValidation.state!.maxLength)],
      postcode: [
        null,
        Validators.maxLength(masterValidation.postcode!.maxLength),
      ],
      country: [
        null,
        Validators.maxLength(masterValidation.country!.maxLength),
      ],
      address1m: [
        null,
        Validators.maxLength(masterValidation.address1m!.maxLength),
      ],
      address2m: [
        null,
        Validators.maxLength(masterValidation.address2m!.maxLength),
      ],
      suburbm: [
        null,
        Validators.maxLength(masterValidation.suburbm!.maxLength),
      ],
      statem: [null, Validators.maxLength(masterValidation.statem!.maxLength)],
      postcodem: [
        null,
        Validators.maxLength(masterValidation.postcodem!.maxLength),
      ],
      countrym: [
        null,
        Validators.maxLength(masterValidation.countrym!.maxLength),
      ],
      phone1: [null, Validators.maxLength(masterValidation.phone1!.maxLength)],
      phone2: [null, Validators.maxLength(masterValidation.phone2!.maxLength)],
      fax: [null, Validators.maxLength(masterValidation.fax!.maxLength)],
      mobile: [null, Validators.maxLength(masterValidation.mobile!.maxLength)],
      email1: [null, Validators.maxLength(masterValidation.email1!.maxLength)],
      web: [null, Validators.maxLength(masterValidation.web!.maxLength)],
      stickyNotes: [
        null,
        Validators.maxLength(masterValidation.stickyNotes!.maxLength),
      ],
      addressTab: ['street'],
    });

    this.subs.sink = this.workSvc.master$
      .pipe(filter((master) => !!master))
      .subscribe((master) => {
        this.contactFormGroup.patchValue(master!, { emitEvent: false });
        this.contactFormGroup.markAllAsTouched();
        this.setupCountryLabels(master!?.country);
      });

    this.subs.sink = this.contactFormGroup.valueChanges.subscribe(
      (model: FormModel) => {
        if (this.contactFormGroup.valid) {
          this.workSvc.updateMasterContact(model);
        }

        this.setupCountryLabels(this.contactFormGroup.value['country']);
      }
    );
  }

  private initViewModel() {
    this.viewModel$ = merge<Partial<ViewModel>>(
      valuesOf<string>(this.contactFormGroup.get('addressTab')!).pipe(
        map((addressTab) => ({ addressTab }))
      ),
      valuesOf<string>(this.contactFormGroup.get('country')!).pipe(
        map((country) => ({
          addressLookup: this.addressSvc.hasLookup(country || 'Australia'),
        }))
      ),
      valuesOf<string>(this.contactFormGroup.get('countrym')!).pipe(
        map((countrym) => ({
          addressLookupm: this.addressSvc.hasLookup(countrym || 'Australia'),
        }))
      ),
      this.authSvc.claims$.pipe(
        map((claims) => ({
          addressLookupClaim: claims.includes(Unity4Claim.AddressLookup)
            ? true
            : false,
        }))
      ),
      this.workSvc.master$.pipe(
        withLatestFrom(this.workSvc.scheduledCampaigns$),
        map(([master, scheduledCampaigns]) => ({
          recordId: master?.recordId,
          campaignId: master?.campaignId,
          ib: scheduledCampaigns
            ?.find((c) => c.campaignId === master?.campaignId)
            ?.actionType.startsWith('IB'),
          phone1: master?.phone1,
          phone2: master?.phone2,
          mobile: master?.mobile,
        }))
      ),
      this.workSvc.obLaunched$.pipe(map((obLaunched) => ({ obLaunched }))),
      this.workSvc.closeDisable$.pipe(
        map((closeDisable) => ({ closeDisable }))
      ),
      this.workSvc.oncall$.pipe(map((oncall) => ({ oncall }))),
      this.workSvc.campaignDialType$.pipe(
        map((dialType) => ({ campaignDialType: dialType }))
      ),
      this.workSvc.testMode$.pipe(map((testFlag) => ({ testMode: testFlag }))),
      this.workSvc.positions$.pipe(map((positions) => ({ positions }))),
      this.workSvc.ibLaunched$.pipe(map((ibLaunched) => ({ ibLaunched }))),
      this.diallerSvc.submitting.pipe(map((submitting) => ({ submitting })))
    ).pipe(scan((acc, value) => ({ ...acc, ...value }), {} as ViewModel));
  }

  private setupCountryLabels(country?: string) {
    this.stateLabel = 'State';
    this.postcodeLabel = 'Postcode';
    this.suburbLabel = 'Suburb';

    if (!country) return;

    switch (country.toLowerCase()) {
      case 'new zealand':
        this.stateLabel = 'City';
        break;
      case 'canada':
        this.stateLabel = 'Province';
        this.postcodeLabel = 'Postal Code';
        this.suburbLabel = 'City';
        break;
      case 'united kingdom':
      case 'uk':
        this.stateLabel = 'County';
        this.suburbLabel = 'Town';
        break;
    }
  }

  addressLookup(mail: boolean = false) {
    const model = this.contactFormGroup.value as FormModel;
    const a = {
      address1: mail ? model.address1m?.trim() : model.address1?.trim(),
      address2: mail ? model.address2m?.trim() : model.address2?.trim(),
      suburb: mail ? model.suburbm?.trim() : model.suburb?.trim(),
      state: mail ? model.statem?.trim() : model.state?.trim(),
      postcode: mail ? model.postcodem?.trim() : model.postcode?.trim(),
      country: mail ? model.countrym?.trim() : model.country?.trim(),
    };
    const initialAddress = `${a.address1}${a.address2 ? ` ${a.address2}` : ''}${
      a.suburb ? ` ${a.suburb}` : ''
    }${a.state ? ` ${a.state}` : ''}${
      a.postcode ? ` ${a.postcode}` : ''
    }`.trim();
    const modalRef = this.modalSvc.open(AddressLookupComponent, {
      size: 'lg',
    });
    modalRef.componentInstance.initialAddress = initialAddress;
    modalRef.componentInstance.country = a.country;

    from(modalRef.result).subscribe((modalResult) => {
      const result = modalResult as AddressLookupResult;
      if (result?.action == 'select' && result?.address) {
        if (mail) {
          this.contactFormGroup.patchValue({
            address1m: result.address.address1,
            address2m: result.address.address2,
            suburbm: result.address.suburb,
            statem: result.address.state,
            postcodem: result.address.postcode,
          });
        } else {
          this.contactFormGroup.patchValue({
            address1: result.address.address1,
            address2: result.address.address2,
            suburb: result.address.suburb,
            state: result.address.state,
            postcode: result.address.postcode,
          });
        }
      }
    });
  }

  closeIB() {
    this.diallerSvc.closeIB();
  }

  closeOB() {
    this.diallerSvc.closeOB();
  }

  dialPhone(phoneNumber: string) {
    this.workSvc.setNumberUsed(phoneNumber);
    this.diallerSvc.callManual(phoneNumber, '', 30);
  }

  next() {
    this.diallerSvc.next();
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  ngOnInit(): void {
    this.initFormGroup();
    this.initViewModel();
  }

  openSearch() {
    this.workSvc.openRecordSearch();
  }

  openStats() {
    this.modalSvc.open(StatsModalComponent, {
      size: 'lg',
    });
  }
}

interface ViewModel {
  address1: string;
  address1m: string;
  addressLookup: boolean;
  addressLookupClaim: boolean;
  addressLookupm: boolean;
  addressTab: string | null;
  campaignDialType: string | undefined;
  campaignId: number | undefined;
  closeDisable: boolean | undefined;
  ib: boolean | undefined;
  ibLaunched: boolean | undefined;
  mobile: string;
  obLaunched: boolean | undefined;
  oncall: boolean | undefined;
  phone1: string;
  phone2: string;
  positions: string[] | undefined;
  recordId: number | undefined;
  submitting: boolean;
  testMode: boolean | undefined;
}

interface FormModel {
  address1: string;
  address1m: string;
  address2: string;
  address2m: string;
  addressTab: string;
  company: string;
  country: string;
  countrym: string;
  email1: string;
  fax: string;
  first: string;
  last: string;
  mobile: string;
  phone1: string;
  phone2: string;
  position: string;
  postcode: string;
  postcodem: string;
  sal: string;
  state: string;
  statem: string;
  stickyNotes: string;
  suburb: string;
  suburbm: string;
  web: string;
}
